import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { MapPin, Clock, Users } from "lucide-react";
import api from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { formatDate, formatTime, formatApiErrorDetail } from "../../lib/format";

export default function PortalClasses() {
  const [classes, setClasses] = useState([]);
  const [mine, setMine] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const [c, m] = await Promise.all([api.get("/classes"), api.get("/registrations/mine")]);
    setClasses(c.data);
    const map = {};
    m.data.forEach((r) => {
      map[r.class_id] = r.status;
    });
    setMine(map);
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const signUp = async (classId) => {
    setBusy(classId);
    try {
      await api.post("/registrations", { class_id: classId });
      toast.success("Request sent — Eliza will confirm your spot.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="portal-classes">
      <div>
        <p className="overline mb-2">The schedule</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Classes</h1>
        <p className="font-sans text-sm text-stone mt-2">
          Request a spot — nothing is confirmed until the studio approves it.
        </p>
      </div>

      <div className="space-y-5">
        {classes.map((c) => {
          const status = mine[c.id];
          const full = c.open_seats <= 0;
          return (
            <div key={c.id} className="paper-card p-7" data-testid={`class-row-${c.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-serif text-2xl text-ink leading-tight">{c.title}</h3>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-stone border border-hairline px-2 py-0.5">
                      {c.track}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono text-sm text-stone">
                    <span>{formatDate(c.date)}</span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={13} /> {formatTime(c.start_time)}–{formatTime(c.end_time)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <MapPin size={13} /> {c.venue}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Users size={13} /> {c.open_seats > 0 ? `${c.open_seats} seats left` : "Full"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  {status ? (
                    <StatusMark status={status} />
                  ) : (
                    <button
                      onClick={() => signUp(c.id)}
                      disabled={busy === c.id}
                      className={full ? "btn-outline text-sm" : "btn-brass text-sm"}
                      data-testid={`signup-${c.id}`}
                    >
                      {busy === c.id ? "Sending…" : full ? "Join waitlist" : "Sign up"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {classes.length === 0 && <p className="font-sans text-sm text-stone">No classes posted yet.</p>}
      </div>
    </div>
  );
}
